import { MousePointerClick } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAsyncResult } from 'react-use-async-result';

import { Capabilities } from '@/auth-capabilities';
import { QueueSummaryIcons } from '@/components/QueueSummaryIcons';
import { PreviewImage } from '@/components/queue/PreviewImage';
import { QueueRegionChip } from '@/components/queue/QueueRegionChip';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useAuth } from '@/context/appContext';
import { RegionScope } from '@/context/RegionScope';
import { fromNow, parseApiTime } from '@/lib/dates';
import { useCombinedGetNextQueue } from '@/services/useCombinedGetNextQueue';
import { CombinedQueueItem, useCombinedQueue } from '@/services/useCombinedQueue';
import { queueItemPath } from '@/utils/queueItemRoute';

export const MyItems = (props: { items: CombinedQueueItem[]; loading: boolean }) => {
  const { items } = props;
  const { getNextQueue, pending } = useCombinedGetNextQueue();
  const getNextItemRes = useAsyncResult<unknown>();
  const queue = useCombinedQueue();
  const { authInfo, hasAuth } = useAuth();
  const navigate = useNavigate();
  const isItemAvailable = queue.items.some(
    item => !item.first_reviewer_user_id || item.first_reviewer_user_id === authInfo?.reviewer_id,
  );

  const handleClickItem = (item: CombinedQueueItem) => {
    navigate(queueItemPath(item.id, item.region, { utm_source: 'owned-queue-table' }), {
      state: {
        queueItem: item,
        region: item.region,
      },
    });
  };

  const handleClickGetNext = (button: string) => {
    getNextItemRes.bind(
      getNextQueue({
        source: `get-next:${button}`,
        medium: 'button',
      }),
    );
  };

  const getNextButton =
    isItemAvailable || getNextItemRes.isPending || pending ? (
      <Button
        loading={getNextItemRes.isPending || pending}
        loadingLabel="Claiming meal"
        variant="danger"
        onClick={() => handleClickGetNext('has-next')}
        startIcon={<MousePointerClick className="size-4" />}
      >
        Get Next
      </Button>
    ) : (
      <Button variant="outline" onClick={() => handleClickGetNext('waiting')}>
        Waiting for meals...
      </Button>
    );

  const canLabel = hasAuth(Capabilities.mpqItemLabel);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>My Items</CardTitle>
        {canLabel && items.length > 0 && getNextButton}
      </CardHeader>
      {getNextItemRes.isError && (
        <div className="px-5">
          <Alert tone="critical" role="alert">
            Unable to claim the next item. Please try again.
          </Alert>
        </div>
      )}
      {items.length === 0 ? (
        <CardContent className="flex min-h-28 items-center justify-center bg-canvas pt-5">
          {canLabel && getNextButton}
        </CardContent>
      ) : (
        <CardContent className="divide-y divide-line px-0 pb-0">
          {items.map(item => (
            <button
              type="button"
              onClick={() => handleClickItem(item)}
              key={item.regionalKey}
              className="flex w-full cursor-pointer items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-brand-subtle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <RegionScope region={item.region} showAlerts={false}>
                <PreviewImage queueItem={item} size="resized" />
              </RegionScope>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <QueueSummaryIcons queue={item} />
                  <QueueRegionChip region={item.region} />
                  <span className="text-xs text-ink-muted">
                    {item.queue_type === 'nlp_queue' ? 'nlp item' : 'photo item'} |{' '}
                    {fromNow(parseApiTime(item.created_time))}
                  </span>
                </span>
              </span>
            </button>
          ))}
        </CardContent>
      )}
    </Card>
  );
};
